import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import ReviewCard from "@/components/base/ReviewCard";
import ShareBar from "./components/ShareBar";
import {
  formatViews,
  getMovieById,
  getPopularReviews,
  getRelatedMovies,
  getReviewExcerpts,
} from "@/utils/catalog";

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(id);

  usePageMeta(
    movie ? `${movie.title} - 苍井空电影详情与影评` : "该内容已下架或不存在 - 苍井空电影大全",
    movie
      ? `${movie.title}是${movie.year}年上映的${movie.genres.join("、")}作品，由${movie.director}执导，${movie.cast.join("、")}主演。本页提供影片基本信息、剧情简介、影迷影评摘录与相关推荐。`
      : "该影视作品可能已下架或不存在，可返回电影分类浏览其他内容。",
  );

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1320px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-2xl leading-none" />
          </span>
          <h1 className="mt-5 font-heading text-xl text-foreground-950">该内容已下架或不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">
            这部作品的资料可能已经调整，返回分类页看看其他作品吧。
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Link
              to="/category"
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-grid-line" />
              返回电影分类
            </Link>
            <Link
              to="/"
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-home-4-line" />
              回到首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const excerpts = getReviewExcerpts(movie.id, 3);
  const popular = getPopularReviews(4);

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden bg-foreground-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title}电影剧照`}
          title={`${movie.title} - 苍井空电影详情`}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-transparent to-foreground-950/40" />

        <div className="relative z-10 mx-auto w-full max-w-[1320px] px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "电影分类", to: "/category" }, { label: movie.title }]} />

          <div className="mt-6 grid gap-6 md:grid-cols-[190px_1fr] md:gap-8 lg:grid-cols-[220px_1fr]">
            <div className="w-[150px] shrink-0 overflow-hidden rounded-lg border border-background-500/30 md:w-full">
              <div className="aspect-[2/3] w-full">
                <img
                  src={movie.cover}
                  alt={`${movie.title}电影封面`}
                  title={`${movie.title} - 苍井空电影`}
                  className="h-full w-full object-top object-cover"
                />
              </div>
            </div>

            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-[11px] text-background-300">
                <span className="rounded-full border border-accent-400/50 px-2.5 py-0.5 text-accent-300">
                  {movie.genres.join(" / ")}
                </span>
                <span>{movie.year} 年</span>
                <span className="h-3 w-px bg-background-500/60" />
                <span>{movie.duration} 分钟</span>
                <span className="h-3 w-px bg-background-500/60" />
                <span>{movie.region}</span>
              </p>

              <h1 className="mt-3 font-heading text-2xl leading-tight text-background-50 md:text-[38px]">
                {movie.title}
              </h1>
              <p className="mt-1 text-xs tracking-[0.12em] text-background-400">{movie.enTitle}</p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200">
                {movie.tagline}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" />
                  <span className="font-heading text-base text-accent-300">{movie.rating.toFixed(1)}</span>
                  <span className="text-background-400">/ 10</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" />
                  {formatViews(movie.views)} 次浏览
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  资料更新于 {movie.updatedAt}
                </span>
              </div>

              <dl className="mt-5 grid gap-x-6 gap-y-2 text-xs text-background-300 sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">编剧</dt>
                  <dd className="text-background-100">{movie.writer}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">主演</dt>
                  <dd className="text-background-100">{movie.cast.join(" / ")}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-400">语言</dt>
                  <dd className="text-background-100">{movie.language}</dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <Link
                  to="/category"
                  className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-400/50 px-4 py-2.5 text-sm text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-arrow-left-line" />
                  返回分类
                </Link>
                <span className="hidden text-[11px] text-background-500 sm:inline">
                  分享：
                </span>
                <ShareBar title={movie.title} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1320px] gap-8 px-4 py-10 md:px-8 md:py-12 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <section aria-labelledby="synopsis">
            <div className="flex items-center gap-3 border-b border-background-200 pb-3">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <h2 id="synopsis" className="font-heading text-lg text-foreground-950 md:text-xl">
                剧情简介
              </h2>
            </div>
            <div className="mt-4 space-y-3.5">
              <p className="text-sm leading-[1.9] text-foreground-700">{movie.summary}</p>
              {movie.synopsis.map((paragraph, index) => (
                <p key={index} className="text-sm leading-[1.9] text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/category?genre=${encodeURIComponent(genre)}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {genre}作品
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-10" aria-labelledby="excerpts">
            <div className="flex flex-wrap items-end justify-between gap-2 border-b border-background-200 pb-3">
              <div className="flex items-center gap-3">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                <h2 id="excerpts" className="font-heading text-lg text-foreground-950 md:text-xl">
                  影评摘录
                </h2>
              </div>
              <Link to="/reviews" className="text-[11px] text-primary-700 hover:text-primary-800">
                浏览全部影评
              </Link>
            </div>

            <div className="mt-4 space-y-3">
              {excerpts.length ? (
                excerpts.map((review) => {
                  const isOwn = review.movieId === movie.id;
                  return (
                    <article
                      key={review.id}
                      className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-[10px] text-foreground-500">
                        <span className={`rounded-full px-2 py-0.5 ${isOwn ? "bg-primary-50 text-primary-700" : "bg-background-200 text-foreground-600"}`}>
                          {isOwn ? "本片影评" : "同题材影评"}
                        </span>
                        <span>{review.date}</span>
                        <span className="flex items-center gap-0.5">
                          <i className="ri-eye-line text-[11px]" />
                          {formatViews(review.views)}
                        </span>
                        <span className="ml-auto">{review.author}</span>
                      </div>
                      <Link to={`/reviews/${review.id}`} className="mt-2 block">
                        <h3 className="font-heading text-[15px] leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                          {review.title}
                        </h3>
                      </Link>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                        {review.excerpt}
                      </p>
                    </article>
                  );
                })
              ) : (
                <p className="rounded-lg border border-dashed border-background-300 px-4 py-6 text-center text-xs text-foreground-500">
                  这部作品暂时还没有收录影评，欢迎投稿分享你的观后感。
                </p>
              )}
            </div>
          </section>

          <section className="mt-10" aria-labelledby="related">
            <div className="flex items-center gap-3 border-b border-background-200 pb-3">
              <span className="h-4 w-1 rounded-full bg-secondary-500" />
              <h2 id="related" className="font-heading text-lg text-foreground-950 md:text-xl">
                相关推荐
              </h2>
              <span className="text-[11px] text-foreground-400">按题材与年代匹配</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} variant="compact" />
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-base text-foreground-950">影片信息</h2>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[
                { label: "上映年份", value: `${movie.year} 年` },
                { label: "片长", value: `${movie.duration} 分钟` },
                { label: "类型", value: movie.genres.join(" / ") },
                { label: "制片地区", value: movie.region },
                { label: "语言", value: movie.language },
                { label: "导演", value: movie.director },
                { label: "编剧", value: movie.writer },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-200/70 pb-2 last:border-none last:pb-0">
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-right text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base text-foreground-950">分享与反馈</h2>
            <p className="mt-1.5 text-[11px] leading-relaxed text-foreground-500">
              分享入口为演示状态，仅支持复制当前页面链接。
            </p>
            <div className="mt-3">
              <ShareBar title={movie.title} />
            </div>
            <Link
              to="/about#feedback"
              className="mt-3 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-100 px-3 py-2 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              <i className="ri-feedback-line text-sm" />
              反馈资料错误
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base text-foreground-950">热门影评</h2>
            <div className="mt-2">
              {popular.map((review) => (
                <ReviewCard key={review.id} review={review} variant="mini" />
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <p className="flex items-center gap-2 text-[11px] tracking-[0.18em] text-accent-800">
              <i className="ri-bookmark-line text-sm" />
              UP NEXT
            </p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              看完之后，不妨读读影迷对同一时期作品的对照分析。
            </p>
            <Link
              to="/reviews"
              className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800"
            >
              进入影评资讯
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}